import React, { useCallback, useEffect, useState } from 'react';
import AdminSideMenu from '../components/UI/AdminSideMenu/AdminSideMenu';
import styles from './Analytics.module.css';
import {
  Box, Grid, Card, CardContent, Typography, TextField, Select, MenuItem, Button, CircularProgress, Chip, Alert,
} from '@mui/material';
import {
  adminListSubscriptions,
  adminSubscriptionStats,
  adminSuppressEmail,
  adminUnsuppressEmail,
  adminExportSubscriptionsCsv,
} from '../components/Email/emailPreferencesApi';

const CATEGORY_LABELS = {
  account_activity: 'Account activity',
  job_alerts: 'Job alerts',
  tips_reminders: 'Tips & reminders',
  promotions: 'Offers & recommendations',
  school_updates: 'School updates',
};

const REASON_LABELS = {
  too_many: 'Too many emails',
  not_relevant: 'Not relevant',
  never_signed_up: 'Never signed up',
  other: 'Other',
};

const SUPPRESS_REASONS = [
  { value: 'admin', label: 'Admin decision' },
  { value: 'complaint', label: 'Spam complaint' },
  { value: 'hard_bounce', label: 'Hard bounce' },
  { value: 'invalid', label: 'Invalid address' },
];

const fmtDate = (d) => (d ? new Date(d).toLocaleString() : '-');

const KpiCard = ({ label, value }) => (
  <Grid item xs={12} sm={6} md={3}>
    <Card className={styles.kpiCard} sx={{ background: '#ffffff' }}>
      <CardContent>
        <Typography className={styles.kpiLabel}>{label}</Typography>
        <Typography className={styles.kpiValue}>{value ?? 0}</Typography>
      </CardContent>
    </Card>
  </Grid>
);

const optedOutCategories = (row) => {
  if (row.globalOptOut) return ['All optional'];
  const cats = row.categories || {};
  return Object.keys(CATEGORY_LABELS).filter((k) => cats[k] === false).map((k) => CATEGORY_LABELS[k]);
};

const EmailSubscriptionsDashboard = () => {
  const token = sessionStorage?.getItem('afd8TvhsdjwiuuvsgjhsAfgsUhjs');
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [status, setStatus] = useState('');
  const [reason, setReason] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ items: [], total: 0 });
  const [stats, setStats] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busyEmail, setBusyEmail] = useState('');
  const [suppressEmail, setSuppressEmail] = useState('');
  const [suppressReason, setSuppressReason] = useState('admin');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => { setDebouncedQ(q.trim()); setPage(1); }, 350);
    return () => clearTimeout(id);
  }, [q]);

  const filters = useCallback(() => {
    const p = {};
    if (debouncedQ) p.q = debouncedQ;
    if (status) p.status = status;
    if (reason) p.reason = reason;
    return p;
  }, [debouncedQ, status, reason]);

  const loadList = useCallback(async () => {
    setLoading(true);
    const res = await adminListSubscriptions(token, { ...filters(), page, limit });
    setLoading(false);
    if (res.ok) setData({ items: res.data.items || [], total: res.data.total || 0 });
    else setNotice({ type: 'error', text: res.error });
  }, [token, filters, page, limit]);

  const loadStats = useCallback(async () => {
    const res = await adminSubscriptionStats(token);
    if (res.ok) setStats(res.data);
  }, [token]);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { loadStats(); }, [loadStats]);

  const refresh = () => { loadList(); loadStats(); };

  const runSuppress = async (email, why) => {
    if (!window.confirm(`Stop ALL emails (except password resets) to ${email}?`)) return;
    setBusyEmail(email);
    const res = await adminSuppressEmail(token, email, why);
    setBusyEmail('');
    if (res.ok) {
      setNotice({ type: 'success', text: `${email} is now suppressed.` });
      setSuppressEmail('');
      refresh();
    } else setNotice({ type: 'error', text: res.error });
  };

  const runUnsuppress = async (email) => {
    if (!window.confirm(`Allow emails to ${email} again? Their own unsubscribe choices still apply.`)) return;
    setBusyEmail(email);
    const res = await adminUnsuppressEmail(token, email);
    setBusyEmail('');
    if (res.ok) {
      setNotice({ type: 'success', text: `${email} is no longer suppressed.` });
      refresh();
    } else setNotice({ type: 'error', text: res.error });
  };

  const exportCsv = async () => {
    setExporting(true);
    const res = await adminExportSubscriptionsCsv(token, filters());
    setExporting(false);
    if (!res.ok) {
      setNotice({ type: 'error', text: res.status === 403 ? 'You do not have permission to export.' : res.error });
      return;
    }
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = `email-subscriptions-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const rows = data.items;
  const total = data.total;
  const s = stats || {};

  return (
    <div className={styles.analyticsContainer}>
      <AdminSideMenu />
      <div className={styles.analyticsContent}>
        <Box className={styles.analyticsHeader}>
          <Box className={styles.headerLeft}>
            <Typography variant="h4" className={styles.pageTitle}>Email Subscriptions</Typography>
            <Typography variant="subtitle1" className={styles.pageSubtitle}>
              Unsubscribes, category opt-outs and suppressed addresses. Addresses without a record receive all emails.
            </Typography>
          </Box>
        </Box>

        {notice && (
          <Alert severity={notice.type} onClose={() => setNotice(null)} sx={{ mb: 2 }}>{notice.text}</Alert>
        )}

        <Grid container spacing={2} className={styles.kpiContainer}>
          <KpiCard label="Addresses with preferences" value={s.total} />
          <KpiCard label="Unsubscribed from all" value={s.unsubscribed} />
          <KpiCard label="Suppressed" value={s.suppressed} />
          <KpiCard label="Category opt-outs" value={Object.values(s.categories || {}).reduce((a, b) => a + b, 0)} />
        </Grid>

        {stats && (
          <Box sx={{ background: '#fff', p: 2, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Opt-outs by category (excluding “unsubscribe all”)</Typography>
            <Box display="flex" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
              {Object.entries(CATEGORY_LABELS).map(([k, label]) => (
                <Chip key={k} label={`${label}: ${s.categories?.[k] ?? 0}`} variant="outlined" />
              ))}
            </Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Unsubscribe reasons</Typography>
            <Box display="flex" flexWrap="wrap" gap={1}>
              {Object.entries(REASON_LABELS).map(([k, label]) => (
                <Chip key={k} label={`${label}: ${s.reasons?.[k] ?? 0}`} variant="outlined" />
              ))}
            </Box>
          </Box>
        )}

        {/* Manual suppression */}
        <Box
          component="form"
          onSubmit={(e) => { e.preventDefault(); if (suppressEmail.trim()) runSuppress(suppressEmail.trim(), suppressReason); }}
          display="flex" flexWrap="wrap" gap={2} alignItems="center"
          sx={{ background: '#fff', p: 2, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', mb: 2 }}
        >
          <Typography variant="subtitle2" sx={{ width: '100%' }}>Suppress an address</Typography>
          <TextField size="small" type="email" placeholder="email@example.com" value={suppressEmail} onChange={(e) => setSuppressEmail(e.target.value)} sx={{ minWidth: 260 }} />
          <Select size="small" value={suppressReason} onChange={(e) => setSuppressReason(e.target.value)}>
            {SUPPRESS_REASONS.map((r) => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
          </Select>
          <Button type="submit" variant="contained" color="error" disabled={!suppressEmail.trim() || Boolean(busyEmail)}>Suppress</Button>
        </Box>

        {/* Filters */}
        <Box display="flex" flexWrap="wrap" gap={2} alignItems="center" sx={{ background: '#fff', p: 2, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <TextField size="small" placeholder="Search email" value={q} onChange={(e) => setQ(e.target.value)} />
          <Select size="small" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} displayEmpty>
            <MenuItem value=""><em>Any status</em></MenuItem>
            <MenuItem value="unsubscribed">Unsubscribed from all</MenuItem>
            <MenuItem value="partial">Partial opt-out</MenuItem>
            <MenuItem value="suppressed">Suppressed</MenuItem>
          </Select>
          <Select size="small" value={reason} onChange={(e) => { setReason(e.target.value); setPage(1); }} displayEmpty>
            <MenuItem value=""><em>Any reason</em></MenuItem>
            {Object.entries(REASON_LABELS).map(([k, label]) => <MenuItem key={k} value={k}>{label}</MenuItem>)}
          </Select>
          <Button variant="outlined" onClick={() => { setQ(''); setStatus(''); setReason(''); setPage(1); }}>Reset</Button>
          <Button variant="outlined" onClick={refresh} disabled={loading}>Refresh</Button>
          <Button variant="contained" onClick={exportCsv} disabled={exporting} sx={{ ml: 'auto', background: '#3E8F93' }}>
            {exporting ? 'Exporting…' : 'Export CSV'}
          </Button>
        </Box>

        <Box className={styles.chartContainer} sx={{ p: 0 }}>
          <div style={{ padding: 16 }}>
            <Typography variant="h6">Records ({total})</Typography>
            {loading ? (
              <Box className={styles.loadingContainer}><CircularProgress /></Box>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#c0d1d457' }}>
                      <th style={{ textAlign: 'left', padding: 8 }}>Email</th>
                      <th style={{ textAlign: 'left', padding: 8 }}>Status</th>
                      <th style={{ textAlign: 'left', padding: 8 }}>Opted out of</th>
                      <th style={{ textAlign: 'left', padding: 8 }}>Reason</th>
                      <th style={{ textAlign: 'left', padding: 8 }}>Updated</th>
                      <th style={{ textAlign: 'left', padding: 8 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const out = optedOutCategories(r);
                      const open = expanded === r.email;
                      return (
                        <React.Fragment key={r.email}>
                          <tr style={{ borderBottom: '1px solid #eee', verticalAlign: 'top' }}>
                            <td style={{ padding: 8, wordBreak: 'break-all' }}>{r.email}</td>
                            <td style={{ padding: 8 }}>
                              <Box display="flex" gap={0.5} flexWrap="wrap">
                                {r.suppressed && <Chip size="small" color="error" label={`Suppressed${r.suppressedReason ? ` (${r.suppressedReason})` : ''}`} />}
                                {r.globalOptOut && <Chip size="small" color="warning" label="Unsubscribed" />}
                                {!r.suppressed && !r.globalOptOut && <Chip size="small" color={out.length ? 'info' : 'success'} label={out.length ? 'Partial' : 'Subscribed'} />}
                              </Box>
                            </td>
                            <td style={{ padding: 8 }}>{out.length ? out.join(', ') : '-'}</td>
                            <td style={{ padding: 8, maxWidth: 280, whiteSpace: 'pre-wrap' }}>
                              {r.lastReason ? REASON_LABELS[r.lastReason] || r.lastReason : '-'}
                              {r.lastReasonText ? <div style={{ color: '#666', fontSize: 13 }}>{r.lastReasonText}</div> : null}
                            </td>
                            <td style={{ padding: 8, whiteSpace: 'nowrap' }}>{fmtDate(r.updatedAt)}</td>
                            <td style={{ padding: 8 }}>
                              <Box display="flex" gap={1} flexWrap="wrap">
                                {r.suppressed ? (
                                  <Button size="small" variant="outlined" disabled={busyEmail === r.email} onClick={() => runUnsuppress(r.email)}>Unsuppress</Button>
                                ) : (
                                  <Button size="small" variant="outlined" color="error" disabled={busyEmail === r.email} onClick={() => runSuppress(r.email, 'admin')}>Suppress</Button>
                                )}
                                <Button size="small" onClick={() => setExpanded(open ? null : r.email)}>{open ? 'Hide history' : 'History'}</Button>
                              </Box>
                            </td>
                          </tr>
                          {open && (
                            <tr style={{ background: '#fafcfc' }}>
                              <td colSpan={6} style={{ padding: '8px 16px' }}>
                                {(r.history || []).length === 0 ? (
                                  <Typography variant="body2" color="text.secondary">No history recorded.</Typography>
                                ) : (
                                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                    <thead>
                                      <tr>
                                        <th style={{ textAlign: 'left', padding: 4 }}>When</th>
                                        <th style={{ textAlign: 'left', padding: 4 }}>Action</th>
                                        <th style={{ textAlign: 'left', padding: 4 }}>Scope</th>
                                        <th style={{ textAlign: 'left', padding: 4 }}>Source</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {[...r.history].reverse().map((h, i) => (
                                        <tr key={i}>
                                          <td style={{ padding: 4 }}>{fmtDate(h.at)}</td>
                                          <td style={{ padding: 4 }}>{h.action}</td>
                                          <td style={{ padding: 4 }}>{h.category ? (CATEGORY_LABELS[h.category] || h.category) : '-'}</td>
                                          <td style={{ padding: 4 }}>{h.source}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                )}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                    {rows.length === 0 && (
                      <tr><td colSpan={6} style={{ padding: 8, color: '#666' }}>No records match these filters.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
            <Box display="flex" justifyContent="flex-end" alignItems="center" gap={2} mt={2}>
              <Typography variant="body2">Page {page} of {Math.max(1, Math.ceil(total / limit))}</Typography>
              <Button size="small" variant="outlined" disabled={page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</Button>
              <Button size="small" variant="outlined" disabled={page * limit >= total || loading} onClick={() => setPage((p) => p + 1)}>Next</Button>
              <Select size="small" value={String(limit)} onChange={(e) => { setLimit(parseInt(e.target.value, 10)); setPage(1); }}>
                {[25, 50, 100].map((n) => <MenuItem key={n} value={String(n)}>{n}/page</MenuItem>)}
              </Select>
            </Box>
          </div>
        </Box>
      </div>
    </div>
  );
};

export default EmailSubscriptionsDashboard;
